import { useCallback, useEffect, useRef, useState } from 'react';
import { getWidgetStores } from '@/entities/widget';
import { notificationService } from '@/lib/services/notification.service';
import type { UseArtifactSaveParams, UseArtifactSaveReturn } from './useArtifactSave.types';
import type { EmailData } from '../../email-flow-widget.types';
import { useTranslation } from 'react-i18next';

/** Whether two email snapshots hold the same content — the "is this saved?" comparison. */
function isSameEmail(a: EmailData, b: EmailData): boolean {
  return (
    a.htmlContent === b.htmlContent &&
    a.subjectLine === b.subjectLine &&
    a.preheaderText === b.preheaderText
  );
}

export function useArtifactSave({
  emailData,
  artifactData,
  agentId,
  widgetSchemaKey,
}: UseArtifactSaveParams): UseArtifactSaveReturn {
  const { t } = useTranslation('contentTest');

  const lastSavedData = useRef<EmailData>(emailData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Store access via the widget binding seam.
  // See `entities/widget/lib/widget-store-bindings`.
  const { widgetStore, chatStore } = getWidgetStores();
  // Subscribe to ONLY the `getActiveVersion` action via selector — the
  // bare `useWidgetStore()` form subscribes to the entire store, so any
  // unrelated widget action (open/close, version switch) would re-run
  // this hook. Selector keeps subscription narrow to a stable action ref.
  //
  // `getActiveVersion` is declared
  // on `WidgetWidgetStoreState` so the selector resolves to the typed
  // action without an inline `as unknown as { getActiveVersion: ... }`
  // cast. The contract is enforced at the wiring site (`main.tsx` →
  // `configureWidgetStores`) — if `@/app/store/widget` ever renames or
  // removes the action, that structural assignment fails at compile
  // time, not silently here.
  const getActiveVersion = widgetStore.useWidgetStore((s) => s.getActiveVersion);
  const setWidgetUnsavedEdits = widgetStore.useWidgetStore((s) => s.setWidgetUnsavedEdits);
  // Distinct from CodeView's own tracker on the same widget — see `setWidgetUnsavedEdits`.
  const dirtySource = `${agentId}:preview`;
  const chatId = chatStore.useChatId();
  const { mutateAsync: createArtifactVersion, isPending } =
    widgetStore.useCreateArtifactVersion() as {
      mutateAsync: (params: {
        chatId: string;
        exchangeId: string;
        key: string;
        name: string;
        kind: string;
        data: Record<string, unknown>;
      }) => Promise<{
        key: string;
        name: string;
        data: Record<string, unknown>;
        version: number;
        exchangeId: string;
      }>;
      isPending: boolean;
    };

  // Track unsaved changes, and mirror them onto the widget store so the chat composer can warn
  // before a prompt is sent: the agent reads the last SAVED artifact, so an unsaved manual edit
  // (and any lock the user set with it) would be silently discarded.
  //
  // The comparison runs against TWO baselines, and it needs both. `lastSavedData` covers the gap
  // between our own save and the store pushing the new version back. `artifactData` covers everything
  // arriving from outside — an agent revision, a version switch, a save made in the code editor. The
  // widget is reconciled rather than remounted when a new version lands (the side panel renders it
  // without a key), so a mount-time ref alone went stale on the first revision and reported dirty for
  // the rest of the session, firing the composer's warning on every prompt. Matching either baseline
  // means nothing is unsaved, which also makes this independent of effect ordering.
  useEffect(() => {
    const changed =
      !isSameEmail(emailData, lastSavedData.current) && !isSameEmail(emailData, artifactData);
    setHasUnsavedChanges(changed);
    setWidgetUnsavedEdits(dirtySource, changed);
  }, [emailData, artifactData, dirtySource, setWidgetUnsavedEdits]);

  // Keep the local baseline in step with what the store now holds, so a later edit is compared
  // against the version the user is actually looking at.
  useEffect(() => {
    lastSavedData.current = artifactData;
  }, [artifactData]);

  // Clear on unmount so a closed widget never keeps the composer warning.
  useEffect(
    () => () => setWidgetUnsavedEdits(dirtySource, false),
    [dirtySource, setWidgetUnsavedEdits],
  );

  const save = useCallback(async () => {
    const activeVersion = getActiveVersion(agentId);

    if (!chatId || !activeVersion || !activeVersion.widget) {
      notificationService.showError(t('emailFlowWidget.useArtifactSave.failedToSaveTemplate'), {
        persist: false,
        autoHideDuration: 3000,
      });
      return;
    }

    try {
      setHasUnsavedChanges(false);
      setWidgetUnsavedEdits(dirtySource, false);
      const res = await createArtifactVersion({
        chatId,
        exchangeId: activeVersion.widget.exchangeId,
        key: widgetSchemaKey,
        // eslint-disable-next-line i18next/no-literal-string -- persisted artifact name that `apps/api` writes with the same literal; getArtifactDisplayName translates it on read
        name: 'Email Flow Widget',
        // eslint-disable-next-line i18next/no-literal-string -- artifact kind wire enum value
        kind: 'email',
        data: {
          ...(activeVersion.widget.data as object),
          htmlContent: emailData.htmlContent,
          preheaderText: emailData.preheaderText,
          subjectLine: emailData.subjectLine,
        },
      });

      // `storeWidgetVersion` +
      // `getActiveVersion` are declared on `WidgetWidgetStoreState`, so
      // we read them off the typed store snapshot — no inline `as
      // unknown as { ... }` cast that silently widens through `unknown`.
      const widgetPanel = widgetStore.useWidgetStore.getState();
      widgetPanel.storeWidgetVersion(
        widgetPanel.getActiveVersion(agentId)?.messageId ?? '',
        agentId,
        {
          key: res.key,
          agentName: res.name,
          data: res.data,
          version: res.version,
          exchangeId: res.exchangeId,
        },
        res.data.subjectLine as string,
      );

      lastSavedData.current = emailData;
      notificationService.showSuccess(
        t('emailFlowWidget.useArtifactSave.templateSavedSuccessfully'),
      );
    } catch {
      setHasUnsavedChanges(true);
      setWidgetUnsavedEdits(dirtySource, true);
      notificationService.showError(t('emailFlowWidget.useArtifactSave.failedToSaveTemplate'), {
        persist: false,
        autoHideDuration: 3000,
      });
    }
  }, [
    agentId,
    chatId,
    createArtifactVersion,
    emailData,
    getActiveVersion,
    setWidgetUnsavedEdits,
    dirtySource,
    t,
    widgetSchemaKey,
    widgetStore,
  ]);

  return { save, hasUnsavedChanges, isPending };
}
